import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useCreateUser, useUpdateUser } from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import type { UserResponse, Role } from '../../lib/resources/types';
import { useState } from 'react';

// Single dialog component for both create + edit. The shape of the form is
// identical except for `initialPassword` (create only) and the `email` field
// being read-only on edit (changing the login identity post-create is a
// separate, riskier flow we haven't built yet).
const userSchema = z.object({
  email: z.string().min(1, 'Required').refine((v) => v.includes('@'), 'Must contain @'),
  legalName: z.string().min(1, 'Required').max(200, 'Too long'),
  role: z.enum(['EMPLOYEE', 'ADMIN']),
  invoicePrefix: z.string().min(1, 'Required').max(20, 'Max 20 chars').regex(/^[A-Z0-9-]+$/, 'A-Z, 0-9, hyphens only'),
  baseSalaryString: z.string().regex(/^\d+(\.\d{1,2})?$/, 'e.g. 4000 or 4000.00'),
  invoiceCounterStartString: z.string().regex(/^\d*$/, 'Digits only').optional(),
  billingAddress: z.string().max(1000, 'Too long').optional(),
  taxId: z.string().max(100, 'Too long').optional(),
  iban: z.string().max(100, 'Too long').optional(),
  initialPassword: z.string().min(8, 'Min 8 chars').max(200, 'Too long').optional(),
});
type UserFormValues = z.infer<typeof userSchema>;

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserResponse | null; // when present → edit mode; absent → create mode
}

export function UserFormDialog({ open, onOpenChange, user }: UserFormDialogProps) {
  const isEdit = !!user;
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const [serverError, setServerError] = useState<string | null>(null);

  const defaults: UserFormValues = {
    email: user?.email ?? '',
    legalName: user?.legalName ?? '',
    role: (user?.role ?? 'EMPLOYEE') as Role,
    invoicePrefix: user?.invoicePrefix ?? '',
    baseSalaryString: user ? (user.baseSalaryCents / 100).toFixed(2) : '',
    invoiceCounterStartString: user ? String(user.invoiceCounterValue) : '',
    billingAddress: user?.billingAddress ?? '',
    taxId: user?.taxId ?? '',
    iban: user?.iban ?? '',
    initialPassword: '',
  };

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: defaults,
  });

  // Re-seed when user prop changes (switching from one row to another).
  useEffect(() => {
    form.reset(defaults);
    setServerError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, open]);

  async function onSubmit(values: UserFormValues) {
    setServerError(null);
    const baseSalaryCents = Math.round(Number(values.baseSalaryString) * 100);
    try {
      if (isEdit && user) {
        await updateUser.mutateAsync({
          id: user.id,
          body: {
            legalName: values.legalName,
            role: values.role,
            invoicePrefix: values.invoicePrefix,
            baseSalaryCents,
            billingAddress: values.billingAddress || undefined,
            taxId: values.taxId || undefined,
            iban: values.iban || undefined,
          },
        });
      } else {
        // Create requires an initial password.
        if (!values.initialPassword || values.initialPassword.length < 8) {
          form.setError('initialPassword', { message: 'Required (min 8 chars)' });
          return;
        }
        await createUser.mutateAsync({
          email: values.email,
          legalName: values.legalName,
          role: values.role,
          invoicePrefix: values.invoicePrefix,
          baseSalaryCents,
          initialPassword: values.initialPassword,
          invoiceCounterStart: values.invoiceCounterStartString
            ? Number(values.invoiceCounterStartString) : undefined,
          billingAddress: values.billingAddress || undefined,
          taxId: values.taxId || undefined,
          iban: values.iban || undefined,
        });
      }
      onOpenChange(false);
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Save failed');
    }
  }

  const pending = isEdit ? updateUser.isPending : createUser.isPending;
  const errors = form.formState.errors;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${user?.legalName}` : 'New user'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update profile, salary, role, or numbering. Email is fixed (it\'s the login identity).'
              : 'Provision a contractor or admin. They sign in with the email + initial password you set here.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email" error={errors.email?.message}>
              <Input
                type="text"
                inputMode="email"
                disabled={isEdit}
                {...form.register('email')}
              />
            </FormField>
            <FormField label="Legal name" error={errors.legalName?.message}>
              <Input {...form.register('legalName')} />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Role" error={errors.role?.message}>
              <select
                className="w-full h-9 px-3 rounded-[--radius-input] border border-border bg-background text-[13px]"
                {...form.register('role')}
              >
                <option value="EMPLOYEE">Employee</option>
                <option value="ADMIN">Admin</option>
              </select>
            </FormField>
            <FormField label="Invoice prefix" error={errors.invoicePrefix?.message}>
              <Input placeholder="ALX" className="font-mono" {...form.register('invoicePrefix')} />
            </FormField>
            <FormField label="Base salary" error={errors.baseSalaryString?.message}>
              <Input inputMode="decimal" placeholder="4000.00" {...form.register('baseSalaryString')} />
            </FormField>
          </div>

          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Initial password" error={errors.initialPassword?.message}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder="min 8 chars"
                  {...form.register('initialPassword')}
                />
              </FormField>
              <FormField label="Invoice counter start" error={errors.invoiceCounterStartString?.message}>
                <Input inputMode="numeric" placeholder="0" {...form.register('invoiceCounterStartString')} />
              </FormField>
            </div>
          )}

          <FormField label="Billing address (optional)" error={errors.billingAddress?.message}>
            <Input {...form.register('billingAddress')} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Tax ID (optional)" error={errors.taxId?.message}>
              <Input className="font-mono" {...form.register('taxId')} />
            </FormField>
            <FormField label="IBAN (optional)" error={errors.iban?.message}>
              <Input className="font-mono" {...form.register('iban')} />
            </FormField>
          </div>

          {serverError && (
            <div className="text-[12px] text-error" role="alert">{serverError}</div>
          )}

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="size-3.5 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {error && <div className="text-[11px] text-error mt-1">{error}</div>}
    </div>
  );
}
